import { useState } from 'react';
import {
  updatePassword, updateProfile, reauthenticateWithCredential, EmailAuthProvider,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { Save, KeyRound } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/lib/auth';

export function Account() {
  const { user, role } = useAuth();
  const [name, setName] = useState(user?.displayName ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 4000); }

  async function saveName() {
    if (!user) return;
    setBusy(true);
    try {
      await updateProfile(user, { displayName: name.trim() });
      if (user.email) await setDoc(doc(db, 'operators', user.email.toLowerCase()), { name: name.trim(), updatedAt: new Date().toISOString() }, { merge: true });
      flash('Name updated.');
    } catch (e) { flash(e instanceof Error ? e.message : 'Could not update.'); }
    finally { setBusy(false); }
  }

  async function changePassword() {
    if (!user?.email) return;
    if (next.length < 6) return flash('New password must be at least 6 characters.');
    if (next !== confirm) return flash('New passwords do not match.');
    setBusy(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      setCurrent(''); setNext(''); setConfirm('');
      flash('Password changed.');
    } catch (e) {
      const code = (e as { code?: string }).code;
      flash(code === 'auth/wrong-password' || code === 'auth/invalid-credential'
        ? 'Your current password is incorrect.'
        : (e instanceof Error ? e.message : 'Could not change password.'));
    } finally { setBusy(false); }
  }

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl text-espresso">My Account</h1>
      <p className="mt-1 font-body text-sm text-muted">{user?.email}{role ? ` · ${role}` : ''}</p>

      <section className="mt-8 border border-line bg-paper p-6">
        <p className="eyebrow mb-4">Your details</p>
        <label className="label">Display name</label>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
        <button onClick={saveName} disabled={busy} className="btn-primary mt-5"><Save size={16} /> Save name</button>
      </section>

      <section className="mt-8 border border-line bg-paper p-6">
        <p className="eyebrow mb-4 flex items-center gap-2"><KeyRound size={15} /> Change password</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">Current password</label>
            <input type="password" className="field" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          </div>
          <div>
            <label className="label">New password</label>
            <input type="password" className="field" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <input type="password" className="field" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
        </div>
        <button onClick={changePassword} disabled={busy} className="btn-primary mt-5"><KeyRound size={16} /> Update password</button>
        <p className="mt-3 font-body text-xs text-muted">If it says recent login required, sign out and back in, then try again.</p>
      </section>

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
